// native
import { map, tap } from 'rxjs/operators';
import { Injectable, isDevMode } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { TitleCasePipe } from '@angular/common';

// addon
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';

// service
import { ApiService } from './api.service';
import { OnPremService } from './onprem.service';
import { UtilityService } from './utility.service';

// models
import {
  WorkItem, WorkItemFilterRequest, WorkItemFilterResponse, WorkItemDefaultConfig, GridItem,
  PaginatedItems, PacsIntegrationRecord, TestBundle, TestListItem, WorkItemListenerResponse,
  CompositeFilterDescriptor, FilterDescriptor, SortDescriptor
} from '../../models';

// constants
import {
  API_PACS_INTEGRATION_QUERY_PATH, API_PACS_INTEGRATION_CONFIGURATION_PATH, WEBSOCKET_PACS_FILTER,
  API_PACS_INTEGRATION_RECORDS_PATH, DEFAULT_PAGE_SIZE, DEFAULT_PAGE_INDEX, PACS_STATUS
} from '../../constants';

// WebSocket normal-closure code (RFC 6455). A clean server close means there is
// nothing left to wait for, so we must not treat it as a recoverable drop.
const SOCKET_NORMAL_CLOSURE = 1000;

@Injectable({
  providedIn: 'root'
})
export class WorkItemService {
  socket: WebSocket;
  activeMwlQueryId = null;

  queryStartTime: Date;
  queryTimeToLive = 20 * 1000;
  queryPingInterval: number;
  QUERY_CHECK_INTERVAL = 2 * 1000;
  QUERY_MESSAGE_TYPE = 'mwl_query_status_update';
  RECONNECT_DELAY_MS = 2 * 1000;
  // Randomised extra delay so many clients reconnecting at once don't stampede.
  RECONNECT_JITTER_MS = 1 * 1000;
  // Once the socket path is exhausted we poll the query status over REST until
  // the query reaches a terminal state or its TTL expires.
  REST_FALLBACK_INTERVAL_MS = 2 * 1000;
  // Overall reconnect budget once a query has been reached at least once.
  MAX_RECONNECT_ATTEMPTS = 10;
  // Tighter budget when the very first connection never opens: a query that is
  // never reachable is most likely denied (expired session / lost access /
  // missing query) and should fail fast instead of looping the full budget.
  MAX_INITIAL_CONNECT_ATTEMPTS = 3;

  resultsStartedAt: Date;
  resultsTimeToLive: number = 86400 * 1000;
  resultsPingInterval: number;
  RESULTS_CHECK_INTERVAL = 300 * 1000;

  workItems$: BehaviorSubject<WorkItem[]> = new BehaviorSubject<WorkItem[]>(null);
  workItemsLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  filteringInProgress = false;

  private listeningQueryId: string | null = null;
  private intentionalClose = false;
  private listeningSuppressed = false;
  private reconnectAttempts = 0;
  private reconnectTimeoutId: number | null = null;
  private restFallbackInterval: number | null = null;
  private everOpenedForQuery = false;

  private clientToServerFilterMap = {
    'eq': 'iexact',
    'contains': 'icontains',
    'startswith': 'istartswith',
    'endswith': 'iendswith',
    'gte': 'gte',
    'lte': 'lte'
  };

  constructor(
    private apiService: ApiService,
    private toastService: ToastrService,
    private translateService: TranslateService,
    private utilityService: UtilityService,
    private onPremService: OnPremService,
  ) { }

  getConfig(): Observable<WorkItemDefaultConfig> {
    return this.apiService.get(API_PACS_INTEGRATION_CONFIGURATION_PATH).pipe(
      map(res => res as WorkItemDefaultConfig),
      tap(res => {
        this.queryTimeToLive = res.mwl_query_ttl_seconds * 1000;
        this.resultsTimeToLive = res.mwl_query_results_ttl_seconds * 1000;
      }));
  }

  getRecords(
    pageSize: number = DEFAULT_PAGE_SIZE,
    pageIndex: number = DEFAULT_PAGE_INDEX,
    sort: SortDescriptor[],
    filter: CompositeFilterDescriptor): Observable<GridItem<PacsIntegrationRecord>> {
    let path = this.calcPathUrl(`${API_PACS_INTEGRATION_RECORDS_PATH}?limit=${pageSize}`, pageSize, pageIndex, sort, filter);

    return this.apiService.get(path).pipe(map((res: PaginatedItems<PacsIntegrationRecord>) => {
      return {
        data: res.results,
        total: res.count
      };
    })) as Observable<GridItem<PacsIntegrationRecord>>;
  }

  restartPacsForRecord(id: number): Observable<void> {
    return this.apiService.post(`${API_PACS_INTEGRATION_RECORDS_PATH}${id}/retry/`).pipe(map(res => res as any));
  }

  filter(body: WorkItemFilterRequest): Observable<WorkItemFilterResponse> {
    if (this.onPremService.onPremPiiEnabled) {
      return this.onPremService.filterWorkItems(body);
    }
    return this.apiService.post(API_PACS_INTEGRATION_QUERY_PATH, body).pipe(map(res => res as WorkItemFilterResponse));
  }

  getMwlQueryStatus(id: string): Observable<WorkItemListenerResponse> {
    return this.apiService.get(`${API_PACS_INTEGRATION_QUERY_PATH}${id}/status/`)
      .pipe(map(res => res as WorkItemListenerResponse));
  }

  beginListening(): void {
    this.listeningSuppressed = false;
  }

  cancelListening(): void {
    this.listeningSuppressed = true;
    this.closeSocket();
  }

  listen(id: string): void {
    if (this.listeningSuppressed) {
      return;
    }

    // Tear down any previous query without emitting a spurious loading=false,
    // since we are about to start a fresh query and set loading=true below.
    this.intentionalClose = true;
    this.stopReconnect();
    this.stopRestFallback();
    this.closeSocketInternal(false, true);
    this.intentionalClose = false;

    this.reconnectAttempts = 0;
    this.everOpenedForQuery = false;
    this.activeMwlQueryId = id;
    this.listeningQueryId = id;
    this.queryStartTime = new Date();
    this.workItemsLoading$.next(true);
    this.setQueryActivityPing();
    this.openSocket(id);
  }

  closeSocket(hasError: boolean = false) {
    this.intentionalClose = true;
    this.stopReconnect();
    this.stopRestFallback();
    this.closeSocketInternal(hasError);
    this.listeningQueryId = null;
    this.intentionalClose = false;
  }

  // Re-establish an in-flight query after the browser regains connectivity.
  // If the socket never comes back the reconnect budget hands off to REST.
  recoverActiveQuery(): void {
    if (!this.listeningQueryId || this.isQueryExpired())
      return;

    if (this.socket?.readyState === WebSocket.OPEN)
      return;

    this.stopReconnect();
    this.stopRestFallback();
    this.reconnectAttempts = 0;
    this.workItemsLoading$.next(true);
    this.openSocket(this.listeningQueryId);
  }

  private openSocket(id: string) {
    const protocol = isDevMode() ? 'ws' : 'wss';
    this.detachSocketHandlers();
    this.socket = new WebSocket(`${protocol}://${window.location.host}${WEBSOCKET_PACS_FILTER}${id}`);
    this.attachSocketHandlers();
  }

  private attachSocketHandlers() {
    this.socket.onopen = () => {
      // A successful handshake proves the query is reachable; refresh the
      // reconnect budget so later mid-session drops get the full allowance.
      this.everOpenedForQuery = true;
      this.reconnectAttempts = 0;
    };

    this.socket.onmessage = event => {
      const message = JSON.parse(event.data);

      if (message.type !== this.QUERY_MESSAGE_TYPE)
        return;

      this.reconnectAttempts = 0;

      this.handleQueryStatus(message.data);
    };

    this.socket.onclose = (event?: CloseEvent) => this.onSocketClose(event);
  }

  // Shared terminal-state handling for both the socket and the REST fallback.
  // Returns true when the query reached a terminal (success/failure) state.
  private handleQueryStatus(data: WorkItemListenerResponse): boolean {
    if (data?.status === PACS_STATUS.AGENT_SUCCESS.value) {
      this.workItems$.next(data.results);
      this.setResultsActivityPing();
      this.closeSocket();
      return true;
    }

    if (data?.status === PACS_STATUS.AGENT_FAILED.value) {
      this.closeSocket(true);
      return true;
    }

    return false;
  }

  private onSocketClose(event?: CloseEvent) {
    if (this.intentionalClose)
      return;

    this.detachSocketHandlers();
    this.socket = null;

    // Clean server-side closure: the query is done, nothing to reconnect for.
    if (event?.code === SOCKET_NORMAL_CLOSURE) {
      this.closeSocket();
      return;
    }

    this.scheduleReconnect();
  }

  private scheduleReconnect() {
    if (this.listeningSuppressed || !this.listeningQueryId || !this.queryStartTime) {
      return;
    }

    if (this.isQueryExpired()) {
      this.closeSocket(true);
      return;
    }

    const maxAttempts = this.everOpenedForQuery
      ? this.MAX_RECONNECT_ATTEMPTS
      : this.MAX_INITIAL_CONNECT_ATTEMPTS;

    if (this.reconnectAttempts >= maxAttempts) {
      // A query we reached at least once is worth polling over REST; one that
      // never opened is most likely denied, so keep the fast-fail behaviour.
      if (this.everOpenedForQuery)
        this.startRestFallback();
      else
        this.closeSocket(true);
      return;
    }

    this.reconnectAttempts++;
    const delay = this.RECONNECT_DELAY_MS + Math.random() * this.RECONNECT_JITTER_MS;
    this.reconnectTimeoutId = window.setTimeout(() => {
      this.reconnectTimeoutId = null;
      if (
        !this.listeningSuppressed
        && this.listeningQueryId
        && this.queryStartTime
        && !this.isQueryExpired()
      ) {
        this.openSocket(this.listeningQueryId);
      }
    }, delay);
  }

  private stopReconnect() {
    if (this.reconnectTimeoutId) {
      window.clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }
  }

  // REST fallback: poll the query status endpoint until the query reaches a
  // terminal state or its TTL expires. Used when the socket cannot recover.
  private startRestFallback() {
    if (!this.listeningQueryId || this.isQueryExpired()) {
      this.closeSocket(true);
      return;
    }

    this.stopRestFallback();
    this.restFallbackInterval = window.setInterval(
      () => this.pollQueryStatusOnce(),
      this.REST_FALLBACK_INTERVAL_MS,
    );
    this.pollQueryStatusOnce();
  }

  private pollQueryStatusOnce() {
    if (!this.listeningQueryId) {
      this.stopRestFallback();
      return;
    }

    if (this.isQueryExpired()) {
      this.closeSocket(true);
      return;
    }

    this.getMwlQueryStatus(this.listeningQueryId).subscribe(
      data => this.handleQueryStatus(data),
      // Transient error (e.g. network blip). Keep polling until a terminal
      // state or the TTL check above closes the query. A 401/403 is handled
      // globally by ApiService (session teardown).
      () => { },
    );
  }

  private stopRestFallback() {
    if (this.restFallbackInterval) {
      window.clearInterval(this.restFallbackInterval);
      this.restFallbackInterval = null;
    }
  }

  private isQueryExpired(): boolean {
    if (!this.queryStartTime)
      return true;

    return Date.now() - this.queryStartTime.getTime() > this.queryTimeToLive;
  }

  private closeSocketInternal(hasError: boolean, suppressLoadingEmit: boolean = false) {
    if (!suppressLoadingEmit)
      this.workItemsLoading$.next(false);

    this.detachSocketHandlers();
    this.socket?.close();
    this.socket = null;
    this.queryStartTime = null;

    if (this.queryPingInterval) {
      window.clearInterval(this.queryPingInterval);
      this.queryPingInterval = null;
    }

    if (hasError) {
      this.workItems$.next([]);
      this.toastService.error(this.translateService.instant('errorFilteringMessage'));
    }
  }

  private detachSocketHandlers() {
    if (!this.socket)
      return;

    this.socket.onopen = null;
    this.socket.onmessage = null;
    this.socket.onclose = null;
    this.socket.onerror = null;
  }

  private setQueryActivityPing() {
    if (this.queryPingInterval)
      window.clearInterval(this.queryPingInterval);

    this.queryPingInterval = window.setInterval(() => {
      if (!this.queryStartTime)
        return;

      const now = new Date().getTime();
      const timeDifference = now - this.queryStartTime.getTime();

      if (timeDifference > this.queryTimeToLive) {
        // closeSocket() already clears this interval; stop here so we do not
        // also evaluate the status-ping branch on a torn-down socket.
        this.closeSocket(true);
        return;
      }

      // ping socket for status if timeout is nearing
      if ((timeDifference + 2 * this.QUERY_CHECK_INTERVAL) > this.queryTimeToLive) {
        if (this.socket?.readyState === WebSocket.OPEN) {
          this.socket.send(JSON.stringify({
            'type': 'mwl_query_status_request'
          }));
        }
      }
    }, this.QUERY_CHECK_INTERVAL);
  }

  private setResultsActivityPing() {
    if (this.resultsPingInterval)
      window.clearInterval(this.resultsPingInterval);

    this.resultsStartedAt = new Date();

    this.resultsPingInterval = window.setInterval(() => {
      if (!this.resultsStartedAt)
        return;

      const now = new Date().getTime();
      const timeDifference = now - this.resultsStartedAt.getTime();
      if (timeDifference > this.resultsTimeToLive) {
        window.clearInterval(this.resultsPingInterval);
        window.location.reload();
      }
    }, this.RESULTS_CHECK_INTERVAL);
  }

  private calcPathUrl(basePath: string, pageSize: number, pageIndex: number, sort: SortDescriptor[], filter: any): string {
    let path = basePath;
    if (pageIndex)
      path = path + `&offset=${pageIndex * pageSize}`;

    filter.filters?.forEach((f: FilterDescriptor) => {
      let value = f.value;

      if (f.field === 'status_changed' && !!f.value) {
        value = `${this.utilityService.convertClientDateToServerDate(f.value)}${f.operator === 'lte' ? '+23:59:59' : ''}`;
        value = value + `&timezone=${this.utilityService.getLocalTimezone()}`;
      }

      path = path + `&${f.field}__${this.clientToServerFilterMap[<string>f.operator]}=${value}`;

      if (f.field == 'patient_name' && !!f.value)
        path = path + `&search=${value}`;
    });

    if (sort[0]?.field && sort[0]?.dir) {
      const prefix = (sort[0].dir === 'asc') ? '' : '-';
      path = path + `&ordering=${prefix}${sort[0].field}`;
    }

    return path;
  }

  isSuccessPacsTest = (item: TestListItem | TestBundle) => !!item.pacs_integration
    && item.pacs_integration.status === PACS_STATUS.AGENT_SUCCESS.value;
  isFailedPacsTest = (item: TestListItem | TestBundle) => !!item.pacs_integration
    && [PACS_STATUS.AGENT_FAILED.value, PACS_STATUS.MESSAGE_FAILED.value].includes(item.pacs_integration.status);
  isPendingPacsTest = (item: TestListItem | TestBundle) => !!item.pacs_integration
    && [PACS_STATUS.MESSAGE_SENT.value, PACS_STATUS.INITIALIZED.value].includes(item.pacs_integration.status);

  getPacsDetails = (item: TestListItem | TestBundle) => {
    if (!item?.pacs_integration?.status) return '';
    if (!PACS_STATUS[item.pacs_integration.status]) return '';
    let value = '';
    value = value + new TitleCasePipe().transform(this.translateService.instant(PACS_STATUS[item.pacs_integration.status]?.translationKey));
    if (item?.pacs_integration?.details)
      value = value + `: ${item.pacs_integration.details}`;
    return value;
  };
}
